import { useMemo } from 'react';

import { InfoOutlined } from '@mui/icons-material';
import { Stack, Typography } from '@mui/material';

import useContactColumns from 'hooks/queries/useContactColumns';
import {
    VoiceContentAction,
    VoiceContentInteraction,
    VoiceContentStateTypes,
    VoiceText,
} from 'pages/ContentVoiceBuilderPage/types';
import theme from 'theme';
import { formatPhoneNumber, getMergeTags, getMessageWithMergeTags } from 'utils';

import ContentPreview from '../ContentPreview';

import VoicePreviewContentAction from './VoicePreviewContentAction';

export type Props = {
    isOpen: boolean;
    onClose: () => void;
    voiceJson: VoiceContentStateTypes['voiceJson'];
    isCloseEnabled?: boolean;
    previewLabel?: string;
};

const VoicePreviewContent = ({
    isOpen,
    onClose,
    voiceJson,
    isCloseEnabled = true,
    previewLabel = 'voice call preview',
}: Props) => {
    const { data: contactColumns, isSuccess: isContactColumnsSuccess } = useContactColumns();

    const mergeTags = useMemo(() => {
        if (isContactColumnsSuccess) return getMergeTags(contactColumns);
    }, [contactColumns, isContactColumnsSuccess]);

    if (!isOpen) return null;

    const getInteractionHeading = (interaction: VoiceContentInteraction) => {
        if (interaction.type === VoiceContentAction.PLAY_MESSAGE) return 'Play Message';
        if (interaction.type === VoiceContentAction.DO_NOT_DISTURB) return 'Opt-Out Message';
        if (interaction.type === VoiceContentAction.TRANSFER)
            return `Transfer to ${formatPhoneNumber(interaction.transferNumber)}`;
    };

    const renderInteraction = (interaction: VoiceContentInteraction) => {
        const message =
            interaction.type === VoiceContentAction.TRANSFER
                ? null
                : getMessageWithMergeTags(mergeTags, (interaction?.message?.[0] as VoiceText).text);

        return (
            <VoicePreviewContentAction
                key={interaction.keypadOption}
                heading={`Press ${interaction.keypadOption}: ${getInteractionHeading(interaction)}`}
                message={message}
            />
        );
    };

    const renderFallbackMessages = () => {
        const timeoutMessage = voiceJson?.fallback?.timeoutMessage;
        const waitTime = voiceJson?.fallback?.waitTime;
        const waitTimeMessage = !!waitTime ? ` | wait ${voiceJson?.fallback?.waitTime} secs` : '';
        const repeatCount = voiceJson?.fallback?.repeat;
        const repeatMessage = !!repeatCount
            ? ` | repeat ${voiceJson?.fallback?.repeat} time${repeatCount === 1 ? '' : 's'}`
            : '';
        const incorrectResponseMessage = voiceJson?.fallback?.incorrectResponseMessage;

        return (
            <>
                {!!timeoutMessage && (
                    <VoicePreviewContentAction
                        heading={`Timeout Message${waitTimeMessage}${repeatMessage}`}
                        message={timeoutMessage}
                    />
                )}
                {!!incorrectResponseMessage && (
                    <VoicePreviewContentAction
                        heading="Incorrect Response Message"
                        message={incorrectResponseMessage}
                    />
                )}
            </>
        );
    };

    const interactions = voiceJson?.voiceCall?.interactions;
    const introMessageText = (voiceJson?.voiceCall.message[0] as VoiceText)?.text;
    const voicemailMessageText = (voiceJson?.voicemail?.message[0] as VoiceText)?.text;

    return (
        <ContentPreview isOpen={isOpen} onClose={onClose} isCloseEnabled={isCloseEnabled} previewLabel={previewLabel}>
            <Stack sx={{ overflow: 'scroll', padding: '0 24px 24px 24px' }}>
                <VoicePreviewContentAction
                    heading="Introduction Message"
                    message={getMessageWithMergeTags(mergeTags, introMessageText)}
                />
                {interactions?.map((interaction) => renderInteraction(interaction))}
                {!!voicemailMessageText && (
                    <VoicePreviewContentAction
                        heading="Voicemail Message"
                        message={getMessageWithMergeTags(
                            mergeTags,
                            (voiceJson?.voicemail?.message[0] as VoiceText)?.text
                        )}
                    />
                )}
                {renderFallbackMessages()}
                <Typography
                    alignItems="center"
                    display="inline-flex"
                    variant="helperText"
                    sx={{ color: theme.palette.text.disabled, paddingTop: 1 }}
                >
                    <InfoOutlined fontSize="inherit" sx={{ marginRight: '4px' }} />
                    messages will be converted to speech
                </Typography>
            </Stack>
        </ContentPreview>
    );
};

export default VoicePreviewContent;
